import { Quote } from '@/model/quotesModel';

import { useMemo } from 'react';

import { isEqual } from 'lodash';
import { adjustQuoteForPremium, adjustQuotesForPremium } from '@/util/quoteAdjustment';
import { enrichQuotesWithROI, type EnrichedQuote } from '@shared';
import {
  inRange,
  matchesTab,
  getBenefitsDuration,
  getInflationProtection,
  MAX_DURATION_YEARS,
} from '@/features/shop-insurance/lib/filter-matchers';
import { calculateGapFillPremium } from '@/features/shop-insurance/lib/coverage-gap-utils';
import type { CombinedFilterType, FilterName } from '@/features/shop-insurance/types';

interface UseAdjustedQuotesParams {
  selectedPremium: number;
  client: Client | undefined;
  selectedTab: ShopInsuranceTab;
  originalQuotes: Quote[] | undefined;
  selectedBenefitDuration: CombinedFilterType;
  selectedInflationProtection: CombinedFilterType;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  selectedFeatureBenefitsFilters?: string[];
  isGapFillMode?: boolean;
  totalCost?: number;
}

interface UseAdjustedQuotesReturn {
  isAdjusted: FilterName[];
  originalTotalQuotes: number;
  adjustedQuotes: EnrichedQuote[];
}

const PREMIUM_ADJUSTMENT_THRESHOLD = 100;

const DEFAULT_FILTERS_VALUES = {
  benefitDuration: [1, MAX_DURATION_YEARS],
  inflationProtection: [0, 5],
};

export const DEFAULT_FILTERS_STATE = {
  benefitDuration: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.benefitDuration,
    aob: DEFAULT_FILTERS_VALUES.benefitDuration,
    cob: DEFAULT_FILTERS_VALUES.benefitDuration,
  },
  inflationProtection: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.inflationProtection,
    aob: DEFAULT_FILTERS_VALUES.inflationProtection,
    cob: DEFAULT_FILTERS_VALUES.inflationProtection,
  },
};

function isFilterApplied(
  filter: CombinedFilterType | null | undefined,
  defaultState: CombinedFilterType,
) {
  return !!filter && !isEqual(filter, defaultState);
}

function shouldAdjustPremium(quotes: Quote[], selectedPremium: number) {
  return quotes.some(
    quote => Math.abs(quote.premiumAmount - selectedPremium) > PREMIUM_ADJUSTMENT_THRESHOLD,
  );
}

function matchesInflationProtection(quote: Quote, filter: CombinedFilterType) {
  const { hasSingle, single, aob, cob } = getInflationProtection(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;
    const rangesAreEqual = minAob === minCob && maxAob === maxCob;

    if (!rangesAreEqual) {
      if (hasSingle && typeof single === 'number') {
        return inRange(single, minAob, maxAob) && inRange(single, minCob, maxCob);
      }
      return inRange(aob, minAob, maxAob) && inRange(cob, minCob, maxCob);
    }
  }

  const [min, max] = filter.type === 'separate' ? filter.aob : filter.combined;

  if (hasSingle && typeof single === 'number') {
    return inRange(single, min, max);
  }

  const isHybrid = aob !== cob;

  if (isHybrid)
    return inRange(aob, min, max) || inRange(cob, min, max);

  return inRange(aob, min, max);
}

function matchesBenefitsDuration(quote: Quote, filter: CombinedFilterType) {
  const { aobYears, cobYears, totalYears } = getBenefitsDuration(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;

    if (aobYears || cobYears) {
      return (
        inRange(aobYears, minAob, maxAob, MAX_DURATION_YEARS) &&
        inRange(cobYears, minCob, maxCob, MAX_DURATION_YEARS)
      );
    }

    const minTotal = minAob + minCob;
    const maxTotal = maxAob + maxCob;

    return inRange(totalYears, minTotal, maxTotal, MAX_DURATION_YEARS * 2);
  }

  const [minCombined, maxCombined] = filter.combined;

  if (aobYears || cobYears) {
    return inRange(aobYears + cobYears, minCombined, maxCombined, MAX_DURATION_YEARS);
  }

  return inRange(totalYears, minCombined, maxCombined, MAX_DURATION_YEARS);
}

function matchesFeatureBenefits(quote: Quote, selectedFilters: string[]): boolean {
  if (selectedFilters.length === 0) return true;

  return selectedFilters.some(filter => {
    if (filter === 'hasGlobalBenefits') return quote.hasGlobalBenefits;
    return false;
  });
}

function buildAppliedFilters(
  inflationFilter: CombinedFilterType,
  durationFilter: CombinedFilterType,
  filtered: Quote[],
  selectedPremium: number,
): FilterName[] {
  const applied: FilterName[] = [];

  if (isFilterApplied(inflationFilter, DEFAULT_FILTERS_STATE.inflationProtection)) {
    applied.push('inflationProtection');
  }

  if (isFilterApplied(durationFilter, DEFAULT_FILTERS_STATE.benefitDuration)) {
    applied.push('benefitDuration');
  }

  if (shouldAdjustPremium(filtered, selectedPremium)) {
    applied.push('premium');
  }
  return applied;
}

function adjustQuotesToFillGap(
  quotes: Quote[],
  totalCost: number,
  client: Client,
  calculationType: ReturnOnInvestmentCalculationType,
): EnrichedQuote[] {
  // First enrichment: get coverage at each quote's original premium to use as the gap-fill baseline.
  const quotesWithBaseCoverage = enrichQuotesWithROI(quotes, client, calculationType);

  const quotesScaledToGapPremium = quotesWithBaseCoverage.map(quote => {
    const baseCoverage = quote.projectedCostCoverage ?? 0;
    if (baseCoverage <= 0) return quote;
    const gapFillPremium = calculateGapFillPremium(
      quote,
      totalCost,
      baseCoverage,
      quote.maxAchievableCoverage ?? totalCost,
    );
    return adjustQuoteForPremium(quote, gapFillPremium);
  });

  // Second enrichment: compute actual coverage at the gap-fill premiums for display.
  return enrichQuotesWithROI(quotesScaledToGapPremium, client, calculationType, {
    includeOriginalROIValues: true,
  });
}

function useAdjustedQuotes({
  client,
  selectedTab,
  originalQuotes,
  selectedPremium,
  selectedBenefitDuration,
  selectedInflationProtection,
  returnOnInvestmentCalculationType,
  selectedFeatureBenefitsFilters = [],
  isGapFillMode = false,
  totalCost = 0,
}: UseAdjustedQuotesParams): UseAdjustedQuotesReturn {
  return useMemo(() => {
    if (!originalQuotes?.length || !client) {
      return { adjustedQuotes: [], isAdjusted: [], originalTotalQuotes: 0 };
    }

    const filteredQuotes = originalQuotes.filter(
      quote =>
        matchesTab(quote, selectedTab) &&
        matchesInflationProtection(quote, selectedInflationProtection) &&
        matchesBenefitsDuration(quote, selectedBenefitDuration) &&
        matchesFeatureBenefits(quote, selectedFeatureBenefitsFilters),
    );

    if (!filteredQuotes.length) {
      return { adjustedQuotes: [], isAdjusted: [], originalTotalQuotes: originalQuotes.length };
    }

    const appliedFilters = buildAppliedFilters(
      selectedInflationProtection,
      selectedBenefitDuration,
      filteredQuotes,
      selectedPremium,
    );

    let withROI: EnrichedQuote[];
    if (isGapFillMode && totalCost > 0) {
      // adjustQuotesToFillGap handles both enrichment passes internally, so we skip
      // the outer enrichQuotesWithROI to avoid a third enrichment at the gap-fill premiums.
      withROI = adjustQuotesToFillGap(filteredQuotes, totalCost, client, returnOnInvestmentCalculationType);
    } else {
      const quotesToProcess = appliedFilters.length
        ? adjustQuotesForPremium(filteredQuotes, selectedPremium)
        : filteredQuotes;
      withROI = enrichQuotesWithROI(quotesToProcess, client, returnOnInvestmentCalculationType, {
        includeOriginalROIValues: true,
      });
    }

    const adjustedQuotes = withROI
      .filter(quote => (quote.projectedCostCoverage ?? 0) > 0)
      .sort((quoteA, quoteB) => (quoteB.roi ?? 0) - (quoteA.roi ?? 0));

    return {
      adjustedQuotes,
      isAdjusted: appliedFilters,
      originalTotalQuotes: originalQuotes.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    selectedTab,
    originalQuotes,
    selectedPremium,
    returnOnInvestmentCalculationType,
    selectedBenefitDuration.type,
    selectedBenefitDuration.combined,
    selectedBenefitDuration.aob,
    selectedBenefitDuration.cob,
    selectedInflationProtection.type,
    selectedInflationProtection.combined,
    selectedInflationProtection.aob,
    selectedInflationProtection.cob,
    selectedFeatureBenefitsFilters,
    isGapFillMode,
    totalCost,
  ]);
}

export { DEFAULT_FILTERS_VALUES, useAdjustedQuotes };
